<template>
  <div class="app-layout">
    <!-- 顶部导航栏 -->
    <header class="app-header">
      <div class="header-container">
        <!-- Logo和网站名 -->
        <div class="header-left">
          <div class="logo" @click="$router.push('/')">
            <img v-if="siteConfig.logo_url" :src="siteConfig.logo_url" :alt="siteConfig.site_name" class="logo-image">
            <img v-else src="/images/logo.png" alt="知享汇资源库" class="logo-image">
            <div class="logo-text">
              <span class="site-name">{{ siteConfig.site_name || '知享汇资源库' }}</span>
              <span class="site-slogan">{{ siteConfig.site_slogan || 'Share & Discover' }}</span>
            </div>
          </div>
        </div>

        <!-- 搜索框 -->
        <div class="header-center">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索资源..."
            prefix-icon="el-icon-search"
            class="search-input"
            @keyup.enter.native="handleSearch"
            @clear="handleClearSearch"
            clearable>
            <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
          </el-input>
        </div>

        <!-- 用户区域 -->
        <div class="header-right">
          <!-- 深色模式切换 -->
          <el-tooltip :content="isDarkMode ? '切换为浅色模式' : '切换为深色模式'" placement="bottom">
            <el-button 
              :icon="isDarkMode ? 'el-icon-sunny' : 'el-icon-moon'" 
              circle 
              @click="toggleDarkMode"
              class="icon-btn">
            </el-button>
          </el-tooltip>

          <!-- 已登录 -->
          <div v-if="isLoggedIn" class="user-area">
            <!-- 消息通知 -->
            <el-popover
              placement="bottom"
              width="400"
              trigger="click"
              v-model="messagePopoverVisible">
              <div class="message-popover">
                <div class="popover-header">
                  <span class="popover-title">
                    <i class="el-icon-bell"></i> 消息通知
                  </span>
                  <el-button type="text" size="small" @click.stop="viewAllMessages">查看全部</el-button>
                </div>
                
                <div class="popover-content">
                  <div v-if="recentMessages.length === 0" class="empty-message">
                    <i class="el-icon-info"></i>
                    <p>暂无新消息</p>
                  </div>
                  <div v-else class="message-items">
                    <div 
                      v-for="msg in recentMessages" 
                      :key="msg.id" 
                      class="message-item"
                      :class="{ 'unread': msg.isRead === 0 }"
                      @click="handleMessageClick(msg)">
                      <div class="message-icon" :class="msg.type">
                        <i :class="getMessageIcon(msg.type)"></i>
                      </div>
                      <div class="message-content">
                        <div class="message-title">
                          {{ msg.title }}
                          <el-tag v-if="msg.isRead === 0" size="mini" type="danger" effect="dark">未读</el-tag>
                        </div>
                        <div class="message-text">{{ msg.content }}</div>
                        <div v-if="msg.resourceName" class="message-link">
                          <i class="el-icon-link"></i>
                          <span>相关资源：{{ msg.resourceName }}</span>
                        </div>
                        <div class="message-time">{{ formatMessageTime(msg.createTime) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="popover-footer" v-if="recentMessages.length > 0">
                  <el-button type="text" size="small" @click="markAllAsRead">
                    <i class="el-icon-check"></i> 全部标为已读
                  </el-button>
                </div>
              </div>
              
              <el-badge :value="unreadCount" :max="99" :hidden="unreadCount === 0" slot="reference" class="notification-badge">
                <el-button 
                  icon="el-icon-bell" 
                  circle 
                  class="icon-btn">
                </el-button>
              </el-badge>
            </el-popover>

            <!-- 用户下拉菜单 -->
            <el-dropdown @command="handleCommand" class="user-dropdown">
              <div class="user-info">
                <el-avatar :src="userInfo.avatar" size="small">
                  <i class="el-icon-user-solid"></i>
                </el-avatar>
                <span class="username">{{ userInfo.nickname || userInfo.username }}</span>
                <i class="el-icon-arrow-down el-icon--right"></i>
              </div>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="profile">
                  <i class="el-icon-user"></i> 个人中心
                </el-dropdown-item>
                <el-dropdown-item command="membership">
                  <i class="el-icon-trophy"></i> 会员中心
                </el-dropdown-item>
                <el-dropdown-item command="submit">
                  <i class="el-icon-upload"></i> 我要分享
                </el-dropdown-item>
                <el-dropdown-item command="logout" divided>
                  <i class="el-icon-switch-button"></i> 退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>

          <!-- 未登录 -->
          <div v-else class="auth-buttons">
            <el-button type="text" @click="$router.push('/login')" class="login-btn">
              <i class="el-icon-user"></i> 登录
            </el-button>
          </div>
        </div>
      </div>
    </header>

    <!-- 主内容区域 -->
    <main class="app-main">
      <router-view />
    </main>

    <!-- 底部 -->
    <footer class="app-footer">
      <div class="footer-content">
        <!-- 左右布局 -->
        <div class="footer-main">
          <!-- 左侧：免责声明 -->
          <div class="footer-left">
            <h3 class="footer-title">免责声明</h3>
            <div class="footer-disclaimer">
              <p>本站所发布的一切资源均收集于互联网用户公开分享，并有网站用户投稿发布，仅限用于学习和研究目的；版权争议与本站无关，您必须在学习后的24个小时之内，从您的设备中彻底删除上述内容。</p>
              <p>不得将上述内容用于商业或者非法用途，否则，一切后果请用户自负。本站信息来自网络，访问和下载本站内容，说明您已同意上述条款。本站内容如侵犯了您的合法权益，请联系站长以便及时清理。</p>
              <p>本站为非盈利性站点，VIP功能仅仅作为用户喜欢本站捐赠打赏功能，所有内容不作为商业行为。如果觉得本站内容不错，强烈建议您【Ctrl+D】将此站收藏或分享给他人！</p>
            </div>
            <div class="footer-links">
              <a href="#" class="footer-link">关于我们</a>
              <span class="footer-divider">|</span>
              <a href="#" class="footer-link">联系我们</a>
              <span class="footer-divider">|</span>
              <a href="#" class="footer-link">使用协议</a>
              <span class="footer-divider">|</span>
              <a href="#" class="footer-link">隐私政策</a>
            </div>
          </div>
          
          <!-- 右侧：联系方式 -->
          <div class="footer-right">
            <!-- 二维码区域 -->
            <div class="qrcode-container">
              <!-- 微信二维码 -->
              <div v-if="contactConfig.wechatQrCode" class="qrcode-item">
                <el-tooltip content="点击查看大图" placement="top">
                  <el-image 
                    :src="contactConfig.wechatQrCode" 
                    :preview-src-list="[contactConfig.wechatQrCode]"
                    class="qrcode-img"
                    fit="contain"
                    @load="handleImageLoad('微信二维码')"
                    @error="handleImageError('微信二维码', contactConfig.wechatQrCode)">
                    <div slot="error" class="image-slot">
                      <i class="el-icon-picture-outline"></i>
                      <p style="font-size: 10px; margin-top: 5px;">加载失败</p>
                    </div>
                  </el-image>
                </el-tooltip>
                <p class="qrcode-label">{{ contactConfig.wechatDesc || '微信扫码' }}</p>
              </div>
              <!-- 公众号二维码 -->
              <div v-if="contactConfig.officialQrCode" class="qrcode-item">
                <el-tooltip content="点击查看大图" placement="top">
                  <el-image 
                    :src="contactConfig.officialQrCode" 
                    :preview-src-list="[contactConfig.officialQrCode]"
                    class="qrcode-img"
                    fit="contain"
                    @load="handleImageLoad('公众号二维码')"
                    @error="handleImageError('公众号二维码', contactConfig.officialQrCode)">
                    <div slot="error" class="image-slot">
                      <i class="el-icon-picture-outline"></i>
                      <p style="font-size: 10px; margin-top: 5px;">加载失败</p>
                    </div>
                  </el-image>
                </el-tooltip>
                <p class="qrcode-label">{{ contactConfig.officialDesc || '关注公众号' }}</p>
              </div>
            </div>
            
            <!-- 联系信息 -->
            <div class="contact-info">
              <h4 class="contact-title">联系我们</h4>
              <div class="contact-items">
                <div v-if="contactConfig.email" class="contact-item">
                  <i class="el-icon-message"></i>
                  <span>邮箱：{{ contactConfig.email }}</span>
                </div>
                <div v-if="contactConfig.phone" class="contact-item">
                  <i class="el-icon-phone"></i>
                  <span>电话：{{ contactConfig.phone }}</span>
                </div>
                <div v-if="contactConfig.wechat" class="contact-item">
                  <i class="el-icon-chat-dot-round"></i>
                  <span>微信：{{ contactConfig.wechat }}</span>
                </div>
                <div v-if="contactConfig.qq" class="contact-item">
                  <i class="el-icon-chat-line-round"></i>
                  <span>QQ：{{ contactConfig.qq }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 版权信息 -->
        <div class="footer-copyright">
          <p>&copy; 2025 {{ siteConfig.site_name || '知享汇资源库' }}. All rights reserved.</p>
          <p v-if="siteConfig.site_icp" class="icp-info">
            <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
              {{ siteConfig.site_icp }}
            </a>
          </p>
        </div>
      </div>
    </footer>

    <!-- 资源代找悬浮按钮 -->
    <transition name="fade">
      <div class="resource-find-float" v-if="isLoggedIn && hasResourceFindPermission" @click="showResourceFindDialog">
        <div class="float-icon">
          <i class="el-icon-search"></i>
        </div>
        <div class="float-text">资源代找</div>
      </div>
    </transition>

    <!-- 返回顶部按钮 -->
    <el-tooltip content="返回顶部" placement="left">
      <el-button type="primary" icon="el-icon-caret-top" circle @click="scrollToTop" class="back-to-top-btn"></el-button>
    </el-tooltip>

    <!-- 消息详情对话框 -->
    <el-dialog
      :title="currentMessage ? currentMessage.title : '消息详情'"
      :visible.sync="messageDetailVisible"
      width="600px"
      center>
      <div v-if="currentMessage" class="message-detail-content">
        <div class="detail-header">
          <el-tag :type="getMessageTypeTag(currentMessage.type)" size="small">
            {{ getMessageTypeName(currentMessage.type) }}
          </el-tag>
          <span class="detail-time">{{ formatMessageTime(currentMessage.createTime) }}</span>
        </div>
        
        <div class="detail-body">
          <div class="detail-text">{{ currentMessage.content }}</div>
        </div>
        
        <div v-if="currentMessage.link" class="detail-footer">
          <el-button type="primary" size="small" @click="goToMessageLink(currentMessage.link)">
            <i class="el-icon-link"></i> 查看详情
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 资源代找对话框 -->
    <el-dialog
      title="资源代找申请"
      :visible.sync="resourceFindDialogVisible"
      width="600px"
      center>
      <el-form :model="resourceFindForm" :rules="resourceFindRules" ref="resourceFindForm" label-width="100px">
        <el-form-item label="资源名称" prop="resourceName">
          <el-input 
            v-model="resourceFindForm.resourceName" 
            placeholder="请输入您需要的资源名称"
            maxlength="200"
            show-word-limit>
          </el-input>
        </el-form-item>
        <el-form-item label="资源类型" prop="resourceType">
          <el-select v-model="resourceFindForm.resourceType" placeholder="请选择资源类型" style="width: 100%">
            <el-option label="软件工具" value="software"></el-option>
            <el-option label="技术文档" value="document"></el-option>
            <el-option label="视频教程" value="video"></el-option>
            <el-option label="源码项目" value="sourcecode"></el-option>
            <el-option label="其他" value="other"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="详细描述" prop="resourceDescription">
          <el-input 
            type="textarea"
            v-model="resourceFindForm.resourceDescription" 
            placeholder="请详细描述您的需求，包括版本、用途等信息"
            :rows="5"
            maxlength="500"
            show-word-limit>
          </el-input>
        </el-form-item>
        <el-form-item label="联系方式" prop="contactInfo">
          <el-input 
            v-model="resourceFindForm.contactInfo" 
            placeholder="请输入您的联系方式（QQ、微信、邮箱等）"
            maxlength="200"
            show-word-limit>
          </el-input>
        </el-form-item>
        <el-alert
          title="温馨提示"
          type="info"
          :closable="false"
          show-icon>
          <template>
            <p>· 我们会在 48 小时内处理您的请求</p>
            <p v-if="membershipInfo">· 当月剩余次数：{{ (membershipInfo.resourceFindCount || 0) - (membershipInfo.usedResourceFind || 0) }} 次</p>
          </template>
        </el-alert>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="resourceFindDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitResourceFind" :loading="submittingFind">提交申请</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'Layout',
  data() {
    return {
      searchKeyword: '',
      isLoggedIn: false,
      userInfo: {
        username: '',
        nickname: '',
        avatar: ''
      },
      unreadCount: 0,
      isDarkMode: false,
      themeConfig: {
        theme_mode: 'light',
        theme_primary_color: '#667eea',
        theme_dark_bg_color: '#1a1a1a',
        theme_dark_text_color: '#e0e0e0'
      },
      contactConfig: {
        wechatQrCode: '',
        officialQrCode: '',
        wechatDesc: '微信扫码',
        officialDesc: '关注公众号',
        email: '',
        phone: '',
        wechat: '',
        qq: ''
      },
      siteConfig: {
        logo_url: '',
        site_name: '知享汇资源库',
        site_slogan: 'Share & Discover',
        site_title: '',  // 浏览器标题
        site_icp: ''  // 网站备案号
      },
      messagePopoverVisible: false,
      recentMessages: [],
      messageDetailVisible: false,
      currentMessage: null,
      showBackToTop: false,
      // 资源代找相关
      hasResourceFindPermission: false,
      membershipInfo: null,
      resourceFindDialogVisible: false,
      submittingFind: false,
      resourceFindForm: {
        resourceName: '',
        resourceType: '',
        resourceDescription: '',
        contactInfo: ''
      },
      resourceFindRules: {
        resourceName: [
          { required: true, message: '请输入资源名称', trigger: 'blur' }
        ],
        resourceType: [
          { required: true, message: '请选择资源类型', trigger: 'change' }
        ],
        resourceDescription: [
          { required: true, message: '请输入详细描述', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    // 先从缓存读取配置，避免闪烁
    this.loadCachedSiteConfig()
    // 新增：优先应用缓存的主题配置，减少首次渲染样式切换造成的闪屏
    this.loadCachedThemeConfig()
    this.checkLoginStatus()
    this.loadUserInfo()
    this.loadThemeConfig()
    this.loadContactConfig()
    this.loadSiteConfig()
    if (this.isLoggedIn) {
      this.loadUnreadCount()
      this.loadRecentMessages()
      this.loadMembershipInfo()
    }
  },
  watch: {
    '$route'() {
      this.checkLoginStatus()
      this.loadUserInfo()
      if (this.isLoggedIn) {
        this.loadUnreadCount()
        this.loadRecentMessages()
        this.loadMembershipInfo()
      }
      // 路由变化时更新标题
      this.updateRouteTitle()
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    this.handleScroll()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    checkLoginStatus() {
      const token = localStorage.getItem('token')
      this.isLoggedIn = !!token
    },
    
    loadUserInfo() {
      if (this.isLoggedIn) {
        const userInfoStr = localStorage.getItem('user_info')
        if (userInfoStr) {
          try {
            this.userInfo = JSON.parse(userInfoStr)
          } catch (e) {
            console.error('解析用户信息失败:', e)
          }
        }
      }
    },

    async loadUnreadCount() {
      try {
        const res = await this.$axios.get('/user/message/unread-count')
        if (res.data.code === 200) {
          this.unreadCount = res.data.data.count || 0
        }
      } catch (error) {
        console.error('加载未读消息数失败:', error)
      }
    },

    handleSearch() {
      const keyword = this.searchKeyword.trim()
      
      // 如果搜索框为空，清除搜索条件返回首页
      if (!keyword) {
        // 如果URL中有keyword参数，清除它
        if (this.$route.query.keyword) {
          this.$router.push({ path: '/' }).catch(err => {
            if (err.name !== 'NavigationDuplicated') {
              console.error(err)
            }
          })
        }
        return
      }
      
      // 检查是否已经在目标页面
      if (this.$route.path === '/' && this.$route.query.keyword === keyword) {
        // 如果已经在目标页面，只需重新加载数据
        this.$emit('search', keyword)
        return
      }
      
      // 否则进行路由跳转
      this.$router.push({ path: '/', query: { keyword } }).catch(err => {
        // 忽略导航重复错误
        if (err.name !== 'NavigationDuplicated') {
          console.error(err)
        }
      })
    },

    handleClearSearch() {
      // 清空搜索框时自动清除搜索条件
      this.searchKeyword = ''
      if (this.$route.query.keyword) {
        this.$router.push({ path: '/' }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error(err)
          }
        })
      }
    },

    handleCommand(command) {
      switch (command) {
        case 'profile':
          // 检查是否已经在个人中心页面，避免重复导航
          if (this.$route.path !== '/profile') {
            this.$router.push('/profile').catch(err => {
              if (err.name !== 'NavigationDuplicated') {
                console.error(err)
              }
            })
          }
          break
        case 'membership':
          // 会员中心
          if (this.$route.path !== '/membership') {
            this.$router.push('/membership').catch(err => {
              if (err.name !== 'NavigationDuplicated') {
                console.error(err)
              }
            })
          }
          break
        case 'submit':
          // 检查是否已经在提交页面，避免重复导航
          if (this.$route.path !== '/submit') {
            this.$router.push('/submit').catch(err => {
              if (err.name !== 'NavigationDuplicated') {
                console.error(err)
              }
            })
          }
          break
        case 'logout':
          this.handleLogout()
          break
      }
    },

    handleLogout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        localStorage.removeItem('token')
        localStorage.removeItem('user_info')
        this.isLoggedIn = false
        this.userInfo = { username: '', nickname: '', avatar: '' }
        // 避免重复导航错误
        if (this.$route.path !== '/login') {
          this.$router.push('/login').catch(err => {
            // 忽略NavigationDuplicated错误，但重新抛出其他错误
            if (err.name !== 'NavigationDuplicated') {
              console.error(err)
            }
          })
        }
      }).catch(() => {})
    },

    async loadThemeConfig() {
      try {
        const res = await this.$axios.get('/config/display')
        if (res.data.code === 200 && res.data.data) {
          const config = res.data.data
          // 直接读取驼峰命名字段
          this.themeConfig = {
            theme_mode: config.themeMode || 'light',
            theme_primary_color: config.themePrimaryColor || '#667eea',
            theme_dark_bg_color: config.themeDarkBgColor || '#1a1a1a',
            theme_dark_text_color: config.themeDarkTextColor || '#e0e0e0'
          }
          
          console.log('[Theme] 加载主题配置:', this.themeConfig)
          // 新增：缓存主题配置，供下次页面加载前预应用
          localStorage.setItem('theme_config_cache', JSON.stringify(this.themeConfig))
          
          // 检查用户自定义的深色模式设置
          const userDarkMode = localStorage.getItem('darkMode')
          const userDarkModeTimestamp = localStorage.getItem('darkModeTimestamp')
          const now = Date.now()
          
          // 如果用户设置超过24小时，则使用系统配置
          if (userDarkMode !== null && userDarkModeTimestamp && (now - parseInt(userDarkModeTimestamp) < 86400000)) {
            // 用户有自定义设置且在24小时内，使用用户设置
            this.isDarkMode = userDarkMode === 'true'
            console.log('[Theme] 使用用户设置:', this.isDarkMode)
          } else {
            // 使用系统配置
            this.isDarkMode = this.themeConfig.theme_mode === 'dark'
            console.log('[Theme] 使用系统配置:', this.isDarkMode)
            // 清除过期的用户设置
            if (userDarkMode !== null) {
              localStorage.removeItem('darkMode')
              localStorage.removeItem('darkModeTimestamp')
            }
          }
          
          this.applyDarkMode()
          this.applyThemeColors()
        }
      } catch (error) {
        console.error('加载主题配置失败:', error)
        // 降级处理：使用localStorage
        const darkMode = localStorage.getItem('darkMode')
        this.isDarkMode = darkMode === 'true'
        this.applyDarkMode()
        // 使用默认主题颜色
        this.applyThemeColors()
      }
    },

    // 新增：从缓存预加载主题配置，减少首屏抖动
    loadCachedThemeConfig() {
      try {
        const cached = localStorage.getItem('theme_config_cache')
        if (cached) {
          const cfg = JSON.parse(cached)
          this.themeConfig = {
            theme_mode: cfg.theme_mode || cfg.themeMode || 'light',
            theme_primary_color: cfg.theme_primary_color || cfg.themePrimaryColor || '#667eea',
            theme_dark_bg_color: cfg.theme_dark_bg_color || cfg.themeDarkBgColor || '#1a1a1a',
            theme_dark_text_color: cfg.theme_dark_text_color || cfg.themeDarkTextColor || '#e0e0e0'
          }
          const userDarkMode = localStorage.getItem('darkMode')
          const userDarkModeTimestamp = localStorage.getItem('darkModeTimestamp')
          const now = Date.now()
          if (userDarkMode !== null && userDarkModeTimestamp && (now - parseInt(userDarkModeTimestamp) < 86400000)) {
            this.isDarkMode = userDarkMode === 'true'
          } else {
            this.isDarkMode = this.themeConfig.theme_mode === 'dark'
          }
          this.applyDarkMode()
          this.applyThemeColors()
        }
      } catch (e) {
        console.warn('预加载主题配置失败:', e)
      }
    },

    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode
      localStorage.setItem('darkMode', this.isDarkMode)
      localStorage.setItem('darkModeTimestamp', Date.now().toString())  // 记录切换时间
      this.applyDarkMode()
      this.applyThemeColors()  // 切换深色模式时也重新应用主题颜色
    },

    applyDarkMode() {
      if (this.isDarkMode) {
        document.body.classList.add('dark-mode')
        document.documentElement.classList.add('dark-mode')
      } else {
        document.body.classList.remove('dark-mode')
        document.documentElement.classList.remove('dark-mode')
      }
    },

    applyThemeColors() {
      // 应用主题颜色到CSS变量
      const root = document.documentElement
      
      // 确保themeConfig已初始化
      if (!this.themeConfig || !this.themeConfig.theme_primary_color) {
        console.warn('[Theme] 主题配置未初始化，使用默认值')
        this.themeConfig = {
          theme_mode: 'light',
          theme_primary_color: '#667eea',
          theme_dark_bg_color: '#1a1a1a',
          theme_dark_text_color: '#e0e0e0'
        }
      }
      
      // 统一应用主色变量（无论深浅色模式）
      const primaryColor = this.themeConfig.theme_primary_color || '#667eea'
      const darkVariant = this.darkenColor(primaryColor, 15)
      const lightVariant = this.lightenColor(primaryColor, 15)
      root.style.setProperty('--theme-primary', primaryColor)
      root.style.setProperty('--theme-primary-dark', darkVariant)
      root.style.setProperty('--theme-primary-light', lightVariant)

      if (this.isDarkMode) {
        // 深色模式：仅调整文本和背景变量
        const bgColor = this.themeConfig.theme_dark_bg_color || '#1a1a1a'
        const textColor = this.themeConfig.theme_dark_text_color || 'rgba(248, 245, 245, 1)'
        root.style.setProperty('--theme-text', textColor)
        root.style.setProperty('--theme-text-rgb', '248, 245, 245')
        root.style.setProperty('--theme-bg', bgColor)
        console.log('[Theme] 深色模式主题应用:', { primary: primaryColor, text: textColor, bg: bgColor })
      } else {
        // 浅色模式：设置默认文本和背景
        root.style.setProperty('--theme-text', '#303133')
        root.style.setProperty('--theme-bg', '#ffffff')
        console.log('[Theme] 浅色模式主题应用:', { primary: primaryColor, dark: darkVariant, light: lightVariant })
      }
      
// 强制重绘已移除，避免页面闪烁
this.$nextTick(() => {
  // 保持样式应用但不触发隐藏/重排
  void document.body.offsetHeight // 轻量读取以确保浏览器应用新样式
})
    },

    // 颜色加深处理
    darkenColor(color, percent) {
      if (!color || !color.startsWith('#')) {
        return color || '#667eea'
      }
      const num = parseInt(color.replace('#', ''), 16)
      const amt = Math.round(2.55 * percent)
      const R = Math.max(0, Math.min(255, (num >> 16) - amt))
      const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) - amt))
      const B = Math.max(0, Math.min(255, (num & 0x0000FF) - amt))
      return '#' + ((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1)
    },

    // 颜色加亮处理
    lightenColor(color, percent) {
      if (!color || !color.startsWith('#')) {
        return color || '#667eea'
      }
      const num = parseInt(color.replace('#', ''), 16)
      const amt = Math.round(2.55 * percent)
      const R = Math.max(0, Math.min(255, (num >> 16) + amt))
      const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt))
      const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt))
      return '#' + ((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1)
    },

    async loadContactConfig() {
      try {
        const res = await this.$axios.get('/config/contact')
        if (res.data.code === 200 && res.data.data) {
          const data = res.data.data
          this.contactConfig = {
            wechatQrCode: data.wechatQrCode || '',
            officialQrCode: data.officialQrCode || '',
            wechatDesc: data.wechatDesc || '微信扫码',
            officialDesc: data.officialDesc || '关注公众号',
            email: data.email || '',
            phone: data.phone || '',
            wechat: data.wechat || '',
            qq: data.qq || ''
          }
        }
      } catch (error) {
        console.error('加载联系方式配置失败:', error)
      }
    },

    handleImageLoad(name) {
      console.log(`✅ ${name} 加载成功`)
    },

    handleImageError(name, url) {
      console.error(`❌ ${name} 加载失败, URL:`, url)
    },

    async loadSiteConfig() {
      try {
        const res = await this.$axios.get('/config/map')
        if (res.data.code === 200 && res.data.data) {
          const config = res.data.data
          this.siteConfig = {
            logo_url: config.logo_url || '',
            site_name: config.site_name || '知享汇资源库',
            site_slogan: config.site_slogan || 'Share & Discover',
            site_title: config.site_title || '',
            site_icp: config.site_icp || ''
          }
          // 缓存配置到localStorage，供下次页面加载时使用
          localStorage.setItem('site_config_cache', JSON.stringify(this.siteConfig))
          // 动态设置浏览器标题
          this.updateDocumentTitle()
        }
      } catch (error) {
        console.error('加载网站配置失败:', error)
      }
    },
    
    // 从缓存加载网站配置，避免初次加载时显示默认logo
    loadCachedSiteConfig() {
      try {
        const cachedConfig = localStorage.getItem('site_config_cache')
        if (cachedConfig) {
          const config = JSON.parse(cachedConfig)
          this.siteConfig = {
            logo_url: config.logo_url || '',
            site_name: config.site_name || '知享汇资源库',
            site_slogan: config.site_slogan || 'Share & Discover',
            site_title: config.site_title || '',
            site_icp: config.site_icp || ''
          }
          // 立即更新标题
          this.updateDocumentTitle()
        }
      } catch (error) {
        console.error('加载缓存配置失败:', error)
      }
    },

    // 更新浏览器标题
    updateDocumentTitle(suffix = '') {
      const baseTitle = this.siteConfig.site_title || this.siteConfig.site_name || '知享汇资源库'
      document.title = suffix ? `${suffix} - ${baseTitle}` : baseTitle
    },

    // 根据路由更新标题
    updateRouteTitle() {
      const routeNameMap = {
        'Home': '',
        'Login': '登录',
        'Register': '注册',
        'Profile': '个人中心',
        'Submit': '提交资源',
        'Membership': '会员中心',
        'MembershipManage': '我的会员'
      }
      const suffix = routeNameMap[this.$route.name] || ''
      this.updateDocumentTitle(suffix)
    },

    async loadRecentMessages() {
      try {
        const res = await this.$axios.get('/user/message/list', {
          params: {
            current: 1,
            size: 5  // 只加载最近5条
          }
        })
        if (res.data.code === 200) {
          this.recentMessages = res.data.data.records || []
        }
      } catch (error) {
        console.error('加载消息列表失败:', error)
      }
    },

    getMessageIcon(type) {
      const iconMap = {
        system: 'el-icon-warning',
        like: 'el-icon-star-on',
        comment: 'el-icon-chat-dot-round',
        download: 'el-icon-download'
      }
      return iconMap[type] || 'el-icon-bell'
    },

    formatMessageTime(time) {
      if (!time) return ''
      const now = new Date()
      const msgTime = new Date(time)
      const diff = now - msgTime
      const minutes = Math.floor(diff / 60000)
      const hours = Math.floor(diff / 3600000)
      const days = Math.floor(diff / 86400000)
      
      if (minutes < 1) return '刚刚'
      if (minutes < 60) return `${minutes}分钟前`
      if (hours < 24) return `${hours}小时前`
      if (days < 7) return `${days}天前`
      return msgTime.toLocaleDateString('zh-CN')
    },

    async handleMessageClick(msg) {
      // 打开消息详情对话框
      this.currentMessage = msg
      this.messageDetailVisible = true
      this.messagePopoverVisible = false
      
      // 标记为已读
      if (msg.isRead === 0) {
        try {
          // 立即更新本地状态，提供即时反馈
          msg.isRead = 1
          
          // 调用后端接口 - 注意路径格式
          await this.$axios.put(`/user/message/read/${msg.id}`)
          
          // 刷新未读数量和消息列表
          await Promise.all([
            this.loadUnreadCount(),
            this.loadRecentMessages()
          ])
        } catch (error) {
          console.error('标记已读失败:', error)
          // 如果失败，恢复状态
          msg.isRead = 0
        }
      }
    },
    
    goToMessageLink(link) {
      this.messageDetailVisible = false
      
      if (!link || link === '#') return
      
      // 跳转到相关页面
      if (this.$route.fullPath !== link && this.$route.path !== link) {
        this.$router.push(link).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error(err)
          }
        })
      }
    },
    
    getMessageTypeName(type) {
      const typeMap = {
        system: '系统通知',
        like: '获赞通知',
        comment: '评论通知',
        download: '下载通知'
      }
      return typeMap[type] || '通知'
    },
    
    getMessageTypeTag(type) {
      const tagMap = {
        system: 'primary',
        like: 'danger',
        comment: 'success',
        download: 'warning'
      }
      return tagMap[type] || 'info'
    },

    viewAllMessages() {
      console.log('viewAllMessages 被调用了!!!')
      console.log('当前路由路径:', this.$route.path)
      console.log('当前tab参数:', this.$route.query.tab)
      
      this.messagePopoverVisible = false
      
      // 如果已经在消息通知tab，直接返回
      if (this.$route.path === '/profile' && this.$route.query.tab === 'messages') {
        console.log('已经在消息通知tab，直接返回')
        return
      }
      
      const targetRoute = '/profile?tab=messages'
      console.log('目标路由:', targetRoute)
      
      // 如果已经在个人中心页面（但不在消息通知tab）
      if (this.$route.path === '/profile') {
        console.log('当前在个人中心，准备切换到消息通知tab')
        // 更新路由并捕获错误
        this.$router.replace(targetRoute).then(() => {
          console.log('路由更新成功')
          // 路由更新成功后立即触发事件
          this.$root.$emit('switchToMessagesTab')
          console.log('已触发switchToMessagesTab事件')
        }).catch(err => {
          console.log('路由更新失败:', err)
          // 忽略 NavigationDuplicated 错误
          if (err.name !== 'NavigationDuplicated') {
            console.error(err)
          }
        })
      } else {
        console.log('不在个人中心页面，正常跳转')
        // 不在个人中心页面，正常跳转
        this.$router.push(targetRoute).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error(err)
          }
        })
      }
    },

    async markAllAsRead() {
      try {
        await this.$axios.put('/user/message/read-all')
        this.$message.success('已全部标记为已读')
        this.loadUnreadCount()
        this.loadRecentMessages()
        this.messagePopoverVisible = false
      } catch (error) {
        this.$message.error('操作失败')
      }
    },
    handleScroll() {
      const y = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
      this.showBackToTop = y > 400
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    
    // 资源代找相关方法
    async loadMembershipInfo() {
      try {
        const res = await this.$axios.get('/membership/info')
        if (res.data.code === 200) {
          this.membershipInfo = res.data.data
          this.hasResourceFindPermission = (this.membershipInfo.resourceFindCount || 0) > 0
        }
      } catch (error) {
        console.error('加载会员信息失败:', error)
      }
    },
    
    showResourceFindDialog() {
      this.resourceFindDialogVisible = true
    },
    
    async submitResourceFind() {
      this.$refs.resourceFindForm.validate(async (valid) => {
        if (!valid) return
        
        this.submittingFind = true
        try {
          const res = await this.$axios.post('/resource-find/submit', this.resourceFindForm)
          
          if (res.data.code === 200) {
            this.$message.success(res.data.message || '提交成功')
            this.resourceFindDialogVisible = false
            this.resetResourceFindForm()
            // 刷新会员信息，更新剩余次数
            this.loadMembershipInfo()
          } else {
            this.$message.error(res.data.message || '提交失败')
          }
        } catch (error) {
          console.error('提交资源代找请求失败:', error)
          this.$message.error(error.response?.data?.message || '提交失败')
        } finally {
          this.submittingFind = false
        }
      })
    },
    
    resetResourceFindForm() {
      this.resourceFindForm = {
        resourceName: '',
        resourceType: '',
        resourceDescription: '',
        contactInfo: ''
      }
      if (this.$refs.resourceFindForm) {
        this.$refs.resourceFindForm.clearValidate()
      }
    }
  }
}
</script>

<style scoped>
.app-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* 顶部导航栏 */
.app-header {
  background: linear-gradient(135deg, var(--theme-primary, #667eea) 0%, var(--theme-primary-dark, #764ba2) 100%);
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3), 0 2px 8px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
  backdrop-filter: blur(10px);
}

.dark-mode .app-header {
  background: var(--theme-bg, #1a1a1a);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.header-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 32px;
  height: 93px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

/* Logo区域 */
.header-left {
  flex-shrink: 0;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;
  padding: 4px 8px;
}

.logo:hover {
  opacity: 0.85;
}

.logo-on-dark {
  background: rgba(255, 255, 255, 0.85);
  border-radius: 10px;
  padding: 6px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
}

.logo-image {
  height: 53px;
  width: auto;
  object-fit: contain;
  transition: all 0.2s ease;
}

.logo:hover .logo-image {
  transform: scale(1.05);
}

.logo-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.site-name {
  font-size: 24px;
  font-weight: 600;
  white-space: nowrap;
  letter-spacing: 0.3px;
  color: #fff;
  line-height: 1.2;
}

.site-slogan {
  font-size: 13px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
  letter-spacing: 0.5px;
  text-transform: uppercase;
  line-height: 1;
}

/* 搜索框区域 */
.header-center {
  flex: 1;
  max-width: 600px;
  min-width: 280px;
}

.search-input {
  width: 100%;
}

.search-input ::v-deep .el-input-group {
  display: table;
  width: 100%;
  border-collapse: separate;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border-radius: 28px;
  overflow: hidden;
  transition: all 0.3s;
}

.search-input:hover ::v-deep .el-input-group {
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
  transform: translateY(-1px);
}

.search-input ::v-deep .el-input__inner {
  background: rgba(255, 255, 255, 0.95);
  border: none;
  height: 42px;
  font-size: 14px;
  padding-left: 50px;
  padding-right: 15px;
  color: #303133;
  transition: all 0.3s;
}

.search-input ::v-deep .el-input__inner::placeholder {
  color: #909399;
}

.search-input ::v-deep .el-input__inner:focus {
  background: white;
}

.search-input ::v-deep .el-input__prefix {
  left: 16px;
  display: flex;
  align-items: center;
  color: var(--theme-primary, #667eea);
  font-size: 18px;
}

.search-input ::v-deep .el-input-group__append {
  background: linear-gradient(135deg, var(--theme-primary, #667eea) 0%, var(--theme-primary-dark, #764ba2) 100%);
  border: none;
  padding: 0;
  width: 42px;
  vertical-align: middle;
}

.search-input:hover ::v-deep .el-input-group__append {
  background: linear-gradient(135deg, var(--theme-primary-light, #7e8ef5) 0%, var(--theme-primary-dark, #8a5db8) 100%);
}

.search-input ::v-deep .el-input-group__append .el-button {
  background: transparent;
  color: white;
  border: none;
  height: 42px;
  width: 42px;
  padding: 0;
  margin: 0;
  font-size: 16px;
  transition: all 0.3s;
}

.search-input ::v-deep .el-input-group__append .el-button:hover {
  transform: scale(1.1);
  color: #ffd700;
}

.search-input ::v-deep .el-input-group__append .el-button i {
  transition: transform 0.3s;
}

.search-input ::v-deep .el-input-group__append .el-button:hover i {
  transform: rotate(90deg);
}

/* 用户区域 */
.header-right {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px; /* 从 16px 减小到 10px */
}

.user-area {
  display: flex;
  align-items: center;
  gap: 10px; /* 从 18px 减小到 10px */
}

.notification-badge {
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.icon-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.2);
  color: white;
  width: 25px !important; /* 缩小到 25px */
  height: 25px !important; /* 缩小到 25px */
  font-size: 11px !important; /* 图标缩小到 11px */
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  min-width: 25px !important;
  margin-left: 12px; /* 左边距增加，离搜索框远一点 */
}

/* Element UI 按钮样式覆盖 */
.icon-btn.el-button.is-circle {
  width: 25px !important;
  height: 25px !important;
  padding: 0 !important;
}

.icon-btn.el-button.is-circle i {
  font-size: 11px !important;
}

/* 深度选择器覆盖 Element UI 默认样式 */
.app-header .icon-btn.el-button--default.is-circle {
  width: 25px !important;
  height: 25px !important;
  min-width: 25px !important;
  padding: 0 !important;
  font-size: 11px !important;
}

.app-header .icon-btn.el-button--default.is-circle i {
  font-size: 11px !important;
}

.app-header .header-right {
  gap: 10px !important;
}

.app-header .user-area {
  gap: 10px !important;
}

.icon-btn i {
  transition: all 0.3s;
  margin: 0;
  line-height: 1;
}

.icon-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.4);
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2), 0 0 0 4px rgba(255, 255, 255, 0.1);
  color: #ffd700;
}

.icon-btn:active {
  transform: translateY(-1px) scale(1.02);
}

.icon-btn:hover i {
  transform: scale(1.1);
}

.user-dropdown {
  cursor: pointer;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 28px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  color: white;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.user-info:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15), 0 0 0 4px rgba(255, 255, 255, 0.1);
}

.user-info .el-avatar {
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.username {
  font-size: 15px;
  font-weight: 600;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

.user-info .el-icon-arrow-down {
  transition: transform 0.3s;
}

.user-info:hover .el-icon-arrow-down {
  transform: translateY(2px);
}

/* 未登录按钮 */
.auth-buttons {
  display: flex;
  align-items: center;
  gap: 8px; /* 从 14px 减小到 8px */
}

/* 登录按钮样式 */
.login-btn {
  color: white !important;
  font-weight: 600;
  font-size: 14px !important; /* 从 15px 减小到 14px */
  padding: 6px 0px !important; /* 修改为 6px 0px */
  transition: all 0.3s;
  border-radius: 20px;
}

.login-btn.el-button--text {
  padding: 6px 0px !important; /* 修改为 6px 0px */
  font-size: 14px !important;
}

.login-btn:hover {
  color: #ffd700 !important;
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

.login-btn i {
  margin-right: 4px;
}

.auth-buttons .el-button--primary {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  color: var(--theme-primary, #667eea);
  border: 2px solid rgba(255, 255, 255, 0.3);
  font-weight: 600;
  font-size: 14px !important; /* 添加字体大小 */
  padding: 9px 18px !important; /* 从 11px 24px 减小 */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 注册按钮小尺寸 */
.auth-buttons .el-button--primary.el-button--small {
  padding: 9px 18px !important;
  font-size: 14px !important;
}

.auth-buttons .el-button--primary:hover {
  background: white;
  color: #764ba2;
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2), 0 0 0 4px rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
}

.auth-buttons .el-button--primary:active {
  transform: translateY(-1px) scale(1.02);
}

/* 下拉菜单样式 */
.user-dropdown ::v-deep .el-dropdown-menu {
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.user-dropdown ::v-deep .el-dropdown-menu__item {
  padding: 12px 20px;
  border-radius: 8px;
  transition: all 0.3s;
  margin: 4px 0;
  color: #303133;
}

.user-dropdown ::v-deep .el-dropdown-menu__item i {
  margin-right: 10px;
  color: var(--theme-primary, #667eea) !important;
  font-size: 16px;
  transition: all 0.3s;
}

.user-dropdown ::v-deep .el-dropdown-menu__item:hover {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.12) 100%);
  color: var(--theme-primary, #667eea) !important;
  transform: translateX(4px);
}

.user-dropdown ::v-deep .el-dropdown-menu__item:hover i {
  transform: scale(1.2);
  color: var(--theme-primary-dark, #764ba2) !important;
}

.user-dropdown ::v-deep .el-dropdown-menu__item--divided {
  border-top: 1px solid rgba(102, 126, 234, 0.1);
  margin-top: 8px;
}

/* 主内容区域 */
.app-main {
  flex: 1;
  background: #f5f7fa;
}

/* 底部 */
.app-footer {
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  color: #ecf0f1;
  padding: 38px 20px 19px;
}

.footer-content {
  max-width: 1400px;
  margin: 0 auto;
}

/* 底部主体：左右布局 */
.footer-main {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 60px;
  margin-bottom: 26px;
  padding-bottom: 26px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* 左侧：免责声明 */
.footer-left {
  flex: 1;
  max-width: calc(37% + 20px); /* 原来的55%缩小三分之一后再增加20px */
}

.footer-title {
  font-size: 17px;
  font-weight: 700;
  color: #ffd700;
  margin: 0 0 15px 0;
  padding-bottom: 9px;
  border-bottom: 3px solid rgba(255, 215, 0, 0.3);
  display: inline-block;
}

.footer-disclaimer {
  margin-bottom: 18px;
}

.footer-disclaimer p {
  margin: 0 0 9px 0;
  font-size: 12px;
  color: #95a5a6;
  line-height: 1.7;
  text-align: justify;
}

.footer-links {
  margin-top: 15px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.footer-link {
  color: #bdc3c7;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.3s;
  position: relative;
}

.footer-link:hover {
  color: #ffd700;
  transform: translateY(-1px);
}

.footer-divider {
  margin: 0 9px;
  color: #7f8c8d;
}

/* 右侧：二维码 */
.footer-right {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 380px;
}

.qrcode-container {
  display: flex;
  gap: 20px;
  justify-content: center;
  padding: 16px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.qrcode-item {
  text-align: center;
  transition: all 0.3s;
}

.qrcode-item:hover {
  transform: translateY(-3px);
}

.qrcode-img {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  display: block;
  background: white;
  padding: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(255, 255, 255, 0.1);
  transition: all 0.3s;
  cursor: pointer;
}

.qrcode-img:hover {
  transform: scale(1.05);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(255, 215, 0, 0.2);
}

/* 二维码图片错误提示 */
.qrcode-img .image-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 30px;
}

.qrcode-label {
  margin: 6px 0 0 0;
  font-size: 12px;
  font-weight: 600;
  color: #bdc3c7;
}

/* 联系信息 */
.contact-info {
  padding: 18px 20px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.contact-title {
  font-size: 14px;
  font-weight: 700;
  color: #ffd700;
  margin: 0 0 11px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid rgba(255, 215, 0, 0.3);
  display: inline-block;
}

.contact-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #bdc3c7;
  font-size: 12px;
  transition: all 0.3s;
  padding: 5px 8px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.02);
}

.contact-item i {
  font-size: 14px;
  color: #667eea;
  flex-shrink: 0;
  width: 18px;
  text-align: center;
}

.contact-item:hover {
  color: #ecf0f1;
  background: rgba(102, 126, 234, 0.1);
  transform: translateX(5px);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
}

.contact-item:hover i {
  color: #ffd700;
  transform: scale(1.15);
}

/* 版权信息 */
.footer-copyright {
  text-align: center;
  padding-top: 6px;
}

.footer-copyright p {
  margin: 0;
  font-size: 12px;
  color: #7f8c8d;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.icp-info {
  margin-top: 4px !important;
}

.icp-info a {
  color: #7f8c8d;
  text-decoration: none;
  transition: color 0.3s;
}

.icp-info a:hover {
  color: var(--theme-primary, #667eea);
  text-decoration: underline;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .app-header {
    position: sticky;
    top: 0;
  }
  
  .header-container {
    height: 60px;
    gap: 8px;
    padding: 0 12px;
    flex-wrap: nowrap;
  }
  
  /* Logo区域优化 */
  .logo {
    gap: 6px;
    padding: 2px 4px;
  }
  
  .logo-image {
    height: 36px;
  }
  
  .site-name {
    font-size: 16px;
    display: none; /* 移动端隐藏网站名 */
  }
  
  .site-slogan {
    display: none;
  }
  
  /* 搜索框优化 */
  .header-center {
    flex: 1;
    min-width: 0;
    max-width: none;
  }
  
  .search-input ::v-deep .el-input-group {
    border-radius: 20px; /* 移动端减小圆角 */
  }
  
  .search-input ::v-deep .el-input__inner {
    font-size: 14px;
    padding: 0 8px 0 35px; /* 左侧增加padding给图标留空间 */
    height: 38px; /* 增加高度到 38px */
  }
  
  .search-input ::v-deep .el-input__prefix {
    left: 10px; /* 调整图标位置 */
    font-size: 16px;
  }
  
  .search-input ::v-deep .el-input-group__append {
    padding: 0;
    width: 38px; /* 设置按钮宽度 */
  }
  
  .search-input ::v-deep .el-input-group__append .el-button {
    padding: 0;
    width: 38px;
    height: 38px;
    font-size: 14px;
  }
  
  /* 用户区域优化 */
  .header-right {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 4px !important; /* 移动端间距进一步缩小到 4px */
  }
  
  /* 用户区域间距 */
  .user-area {
    gap: 4px !important;
  }
  
  .icon-btn {
    width: 25px !important;
    height: 25px !important;
    padding: 0 !important;
    font-size: 11px !important;
  }
  
  .icon-btn.el-button.is-circle {
    width: 25px !important;
    height: 25px !important;
  }
  
  .icon-btn.el-button.is-circle i {
    font-size: 11px !important;
  }
  
  .username {
    display: none; /* 移动端隐藏用户名 */
  }
  
  .user-info {
    gap: 4px;
  }
  
  .user-info .el-avatar {
    width: 32px;
    height: 32px;
  }
  
  /* 未登录按钮 */
  .auth-buttons {
    display: flex;
    gap: 4px !important; /* 移动端按钮间距缩小到 4px */
  }
  
  .auth-buttons .el-button {
    padding: 6px 0px !important; /* 修改为 6px 0px */
    font-size: 13px;
  }
  
  .login-btn {
    padding: 6px 0px !important; /* 修改为 6px 0px */
  }
  
  /* 消息通知 */
  .notification-badge ::v-deep .el-badge__content {
    font-size: 10px;
  }
  
  /* 底部响应式 */
  .footer-main {
    flex-direction: column;
    gap: 30px;
  }
  
  .footer-left {
    max-width: 100%;
  }
  
  .footer-disclaimer {
    font-size: 12px;
  }
  
  .qrcode-container {
    justify-content: center;
  }
  
  .qrcode-img {
    width: 100px;
    height: 100px;
  }
}

/* 小屏手机进一步优化 */
@media (max-width: 480px) {
  .header-container {
    height: 56px;
    padding: 0 10px;
    gap: 6px;
  }
  
  .logo-image {
    height: 32px;
  }
  
  .search-input ::v-deep .el-input-group {
    border-radius: 18px;
  }
  
  .search-input ::v-deep .el-input__inner {
    font-size: 13px;
    height: 34px;
    padding: 0 6px 0 32px; /* 小屏进一步调整 */
  }
  
  .search-input ::v-deep .el-input__prefix {
    left: 8px;
    font-size: 14px;
  }
  
  .search-input ::v-deep .el-input-group__append {
    width: 34px;
  }
  
  .search-input ::v-deep .el-input-group__append .el-button {
    width: 34px;
    height: 34px;
    font-size: 13px;
  }
  
  .icon-btn {
    width: 24px !important;
    height: 24px !important;
    font-size: 10px !important;
  }
  
  .icon-btn.el-button.is-circle {
    width: 24px !important;
    height: 24px !important;
  }
  
  .icon-btn.el-button.is-circle i {
    font-size: 10px !important;
  }
  
  .user-info .el-avatar {
    width: 30px;
    height: 30px;
  }
}

/* 深色模式样式 */
.dark-mode .app-main {
  background: #1a1a1a;
}

/* 深色模式 - 搜索框 */
.dark-mode .search-input ::v-deep .el-input__inner {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
  color: #e8e8e8;
}

.dark-mode .search-input ::v-deep .el-input__inner::placeholder {
  color: #8a8a8a;
}

.dark-mode .search-input ::v-deep .el-input__inner:focus {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.25);
}

.dark-mode .search-input ::v-deep .el-input__prefix {
  color: #8ea4f5;
}

.dark-mode .search-input ::v-deep .el-input-group {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

.dark-mode .search-input:hover ::v-deep .el-input-group {
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.4);
}

/* 深色模式 - 下拉菜单 */
.dark-mode .user-dropdown ::v-deep .el-dropdown-menu {
  background: #2a2a2a;
  border-color: rgba(102, 126, 234, 0.2);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.dark-mode .user-dropdown ::v-deep .el-dropdown-menu__item {
  color: #e8e8e8;
}

.dark-mode .user-dropdown ::v-deep .el-dropdown-menu__item:hover {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%);
  color: #8ea4f5;
}

.dark-mode .user-dropdown ::v-deep .el-dropdown-menu__item i {
  color: var(--theme-primary, #8ea4f5);
}

.dark-mode .user-dropdown ::v-deep .el-dropdown-menu__item:hover i {
  color: #a8bbf7;
}

.dark-mode .user-dropdown ::v-deep .el-dropdown-menu__item--divided {
  border-top-color: rgba(102, 126, 234, 0.2);
}

.dark-mode .app-footer {
  background: #1c1c1c;
}

.dark-mode .footer-main {
  border-bottom-color: #2d2d2d;
}

.dark-mode .footer-title {
  color: var(--theme-primary, #667eea);
}

.dark-mode .footer-link {
  color: #b0b0b0;
}

.dark-mode .footer-link:hover {
  color: var(--theme-primary, #667eea);
}

.dark-mode .footer-divider {
  color: #4a4a4a;
}

.dark-mode .footer-disclaimer p {
  color: #6a6a6a;
}

.dark-mode .qrcode-label {
  color: #8a8a8a;
}

.dark-mode .contact-title {
  color: #8ea4f5;
}

.dark-mode .contact-item {
  color: #8a8a8a;
}

.dark-mode .contact-item:hover {
  color: #b0b0b0;
}

.dark-mode .footer-copyright p {
  color: #5a5a5a;
}

.dark-mode .icp-info a {
  color: #6a6a6a;
}

.dark-mode .icp-info a:hover {
  color: var(--theme-primary, #8ea4f5);
}

/* 消息弹窗样式 */
.message-popover {
  padding: 0;
}

.popover-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e4e7ed;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8edf3 100%);
}

.popover-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 6px;
}

.popover-title i {
  color: var(--theme-primary, #667eea);
  font-size: 16px;
}

.popover-content {
  max-height: 400px;
  overflow-y: auto;
}

.empty-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-message i {
  font-size: 48px;
  color: #dcdfe6;
  margin-bottom: 12px;
}

.empty-message p {
  margin: 0;
  font-size: 14px;
  color: #909399;
}

.message-items {
  padding: 8px 0;
}

.message-item {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.3s;
  border-bottom: 1px solid #f0f0f0;
  position: relative;
}

.message-item:last-child {
  border-bottom: none;
}

.message-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: transparent;
  transition: all 0.3s;
}

.message-item:hover {
  background: linear-gradient(90deg, rgba(102, 126, 234, 0.05) 0%, rgba(255, 255, 255, 0) 100%);
}

.message-item:hover::before {
  background: linear-gradient(180deg, var(--theme-primary, #667eea) 0%, var(--theme-primary-dark, #764ba2) 100%);
}

.message-item.unread {
  background: linear-gradient(90deg, rgba(102, 126, 234, 0.08) 0%, rgba(255, 255, 255, 0) 100%);
}

.message-item.unread::before {
  background: var(--theme-primary, #667eea);
}

.message-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: white;
}

.message-icon.system {
  background: linear-gradient(135deg, var(--theme-primary, #667eea) 0%, var(--theme-primary-dark, #764ba2) 100%);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.message-icon.like {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  box-shadow: 0 4px 12px rgba(245, 87, 108, 0.3);
}

.message-icon.comment {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  box-shadow: 0 4px 12px rgba(79, 172, 254, 0.3);
}

.message-icon.download {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  box-shadow: 0 4px 12px rgba(67, 233, 123, 0.3);
}

.message-content {
  flex: 1;
  min-width: 0;
}

.message-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-text {
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
  margin-bottom: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.message-link {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--theme-primary, #667eea);
  margin-bottom: 6px;
  padding: 4px 8px;
  background: rgba(102, 126, 234, 0.08);
  border-radius: 4px;
  border-left: 2px solid var(--theme-primary, #667eea);
  transition: all 0.3s;
}

.message-link i {
  font-size: 14px;
}

.message-link span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-item:hover .message-link {
  background: rgba(102, 126, 234, 0.12);
  color: var(--theme-primary-dark, #764ba2);
  border-left-color: var(--theme-primary-dark, #764ba2);
}

.message-time {
  font-size: 12px;
  color: #909399;
}

.popover-footer {
  padding: 12px 16px;
  border-top: 1px solid #e4e7ed;
  text-align: center;
  background: #fafafa;
}

.popover-footer .el-button {
  color: var(--theme-primary, #667eea);
  font-weight: 500;
}

.popover-footer .el-button:hover {
  color: var(--theme-primary-dark, #764ba2);
}

/* 深色模式消息弹窗 */
.dark-mode .message-popover {
  background: #2a2a2a;
}

.dark-mode .popover-header {
  background: linear-gradient(135deg, rgba(42, 42, 42, 0.95) 0%, rgba(35, 35, 35, 0.98) 100%);
  border-bottom-color: rgba(102, 126, 234, 0.2);
}

.dark-mode .popover-title {
  color: #e8e8e8;
}

.dark-mode .popover-content {
  background: #2a2a2a;
}

.dark-mode .empty-message {
  color: #8a8a8a;
}

.dark-mode .empty-message i {
  color: #5a5a5a;
}

.dark-mode .empty-message p {
  color: #8a8a8a;
}

.dark-mode .message-item {
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

.dark-mode .message-item:hover {
  background: linear-gradient(90deg, rgba(102, 126, 234, 0.15) 0%, rgba(255, 255, 255, 0) 100%);
}

.dark-mode .message-item.unread {
  background: linear-gradient(90deg, rgba(102, 126, 234, 0.2) 0%, rgba(255, 255, 255, 0) 100%);
}

.dark-mode .message-title {
  color: #e8e8e8;
}

.dark-mode .message-text {
  color: #b8b8b8;
}

.dark-mode .message-link {
  color: #8ea4f5;
  background: rgba(102, 126, 234, 0.15);
  border-left-color: #8ea4f5;
}

.dark-mode .message-item:hover .message-link {
  background: rgba(102, 126, 234, 0.25);
  color: #a8bbf7;
  border-left-color: #a8bbf7;
}

.dark-mode .message-time {
  color: #8a8a8a;
}

.dark-mode .popover-footer {
  background: rgba(30, 30, 30, 0.95);
  border-top-color: rgba(102, 126, 234, 0.2);
}

/* 深色模式 Popover 组件 */
.dark-mode .el-popover,
.dark-mode .el-popper {
  background: #2a2a2a !important;
  border-color: rgba(102, 126, 234, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6) !important;
}

.dark-mode .el-popover .popper__arrow,
.dark-mode .el-popper .popper__arrow {
  border-bottom-color: #2a2a2a !important;
}

.dark-mode .el-popover .popper__arrow::after,
.dark-mode .el-popper .popper__arrow::after {
  border-bottom-color: #2a2a2a !important;
}

.dark-mode .el-popover[x-placement^="bottom"] .popper__arrow::after,
.dark-mode .el-popper[x-placement^="bottom"] .popper__arrow::after {
  border-bottom-color: #2a2a2a !important;
}

.dark-mode .el-popover[x-placement^="top"] .popper__arrow::after,
.dark-mode .el-popper[x-placement^="top"] .popper__arrow::after {
  border-top-color: #2a2a2a !important;
}

.dark-mode .el-popover[x-placement^="left"] .popper__arrow::after,
.dark-mode .el-popper[x-placement^="left"] .popper__arrow::after {
  border-left-color: #2a2a2a !important;
}

.dark-mode .el-popover[x-placement^="right"] .popper__arrow::after,
.dark-mode .el-popper[x-placement^="right"] .popper__arrow::after {
  border-right-color: #2a2a2a !important;
}

/* 深色模式滚动条 */
.dark-mode .popover-content::-webkit-scrollbar {
  width: 8px;
}

.dark-mode .popover-content::-webkit-scrollbar-track {
  background: #1a1a1a;
  border-radius: 4px;
}

.dark-mode .popover-content::-webkit-scrollbar-thumb {
  background: #4a4a4a;
  border-radius: 4px;
}

.dark-mode .popover-content::-webkit-scrollbar-thumb:hover {
  background: #5a5a5a;
}

/* 消息详情对话框样式 */
.message-detail-content {
  padding: 20px 0;
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f2f5;
  margin-bottom: 20px;
}

.detail-time {
  font-size: 13px;
  color: #909399;
}

.detail-body {
  min-height: 120px;
  max-height: 400px;
  overflow-y: auto;
}

.detail-text {
  font-size: 14px;
  line-height: 1.8;
  color: #606266;
  white-space: pre-wrap;
  word-break: break-word;
}

.detail-footer {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f2f5;
  text-align: right;
}

/* 深色模式消息详情 */
.dark-mode .message-detail-content .detail-header {
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

.dark-mode .message-detail-content .detail-time {
  color: #8a8a8a;
}

.dark-mode .message-detail-content .detail-text {
  color: #b8b8b8;
}

.dark-mode .message-detail-content .detail-footer {
  border-top-color: rgba(255, 255, 255, 0.1);
}
.back-to-top {
  position: fixed;
  right: 24px;
  bottom: 40px;
  z-index: 1100;
}

.back-to-top-btn {
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.35);
}

.dark-mode .back-to-top-btn {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
}

/* 深色模式下的注册按钮 */
.dark-mode .auth-buttons .el-button--primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  border: 2px solid rgba(102, 126, 234, 0.3) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
}

.dark-mode .auth-buttons .el-button--primary:hover {
  background: linear-gradient(135deg, #7a8df0 0%, #8b5fb5 100%) !important;
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4), 0 0 0 4px rgba(102, 126, 234, 0.1) !important;
  border-color: rgba(102, 126, 234, 0.5) !important;
}

.dark-mode .auth-buttons .el-button--primary:active {
  transform: translateY(-1px) scale(1.02);
}

/* 资源代找悬浮按钮 */
.resource-find-float {
  position: fixed;
  right: 30px;
  bottom: 120px;
  width: 60px;
  height: 60px;
  background: var(--theme-primary, #667eea);
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  cursor: pointer;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.resource-find-float:hover {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
}

.resource-find-float .float-icon {
  font-size: 22px;
  color: white;
  margin-bottom: 1px;
}

.resource-find-float .float-text {
  font-size: 10px;
  color: white;
  font-weight: 500;
  letter-spacing: 0px;
  line-height: 1;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* 深色模式下的资源代找按钮 */
.dark-mode .resource-find-float {
  background: var(--theme-primary, #667eea);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}

.dark-mode .resource-find-float:hover {
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.6);
}
</style>
